/*----------------------------------------------------------------------------*\
  #OVERFLOW MENU & BOTTOM DRAWER FOR ACTIONS
\*----------------------------------------------------------------------------*/

const DESKTOP_BREAKPOINT = 1024;
const VIEWPORT_MARGIN = 16;

let _repositionMenu = null;

// Private: Reset a menu's inline positioning back to its CSS default
const _clearMenuPosition = menu => {
  menu.style.position = '';
  menu.style.top = '';
  menu.style.bottom = '';
  menu.style.left = '';
  menu.style.right = '';
  menu.style.maxHeight = '';
  menu.style.overflowY = '';
};

// Private: Stop tracking scroll/resize for the currently open menu
const _stopTrackingMenuPosition = () => {
  if (!_repositionMenu) return;

  window.removeEventListener('scroll', _repositionMenu, true);
  window.removeEventListener('resize', _repositionMenu);
  _repositionMenu = null;
};

// Private: Pin the menu to the viewport, flipping/clamping so it never
// overflows off-screen (which is what forced a scrollbar before)
const _positionMenu = (button, menu) => {
  _clearMenuPosition(menu);

  const buttonRect = button.getBoundingClientRect();
  const menuRect = menu.getBoundingClientRect();

  const spaceBelow = window.innerHeight - buttonRect.bottom;
  const spaceAbove = buttonRect.top;
  const openUp = menuRect.height > spaceBelow - VIEWPORT_MARGIN && spaceAbove > spaceBelow;

  const maxHeight = (openUp ? spaceAbove : spaceBelow) - VIEWPORT_MARGIN * 2;
  if (menuRect.height > maxHeight) {
    menu.style.maxHeight = `${Math.max(maxHeight, 0)}px`;
    menu.style.overflowY = 'hidden';
  }

  let left = buttonRect.right - menuRect.width;
  left = Math.min(left, window.innerWidth - menuRect.width - VIEWPORT_MARGIN);
  left = Math.max(left, VIEWPORT_MARGIN);

  menu.style.position = 'fixed';
  menu.style.left = `${left}px`;

  if (openUp) {
    menu.style.top = `${buttonRect.top - Math.min(menuRect.height, maxHeight) - VIEWPORT_MARGIN}px`;
  } else {
    menu.style.top = `${buttonRect.bottom + VIEWPORT_MARGIN}px`;
  }
};

// Public: Close every open overflow menu
export function closeAllMenus() {
  document.querySelectorAll('.c-overflowMenu').forEach(menu => {
    menu.style.display = 'none';
    _clearMenuPosition(menu);
  });

  _stopTrackingMenuPosition();
}

// Public: Close every open mobile drawer (optionally excluding one)
export function closeAllDrawers(exceptDrawer = null) {
  document.querySelectorAll('.c-table__actionsInner').forEach(container => {
    const drawer = container.querySelector('.c-drawer');
    const backdrop = container.querySelector('.c-drawer__backdrop');

    if (!drawer || !backdrop) return;
    if (drawer === exceptDrawer) return;

    drawer.classList.remove('open');
    backdrop.classList.remove('open');

    setTimeout(() => {
      drawer.style.display = 'none';
      backdrop.style.display = 'none';
    }, 300);
  });

  document.body.style.overflow = '';
}

// Default module export
export default function(options) {
  // Private: Default settings object
  const _defaults = {
    btnSelector: '.c-overflowMenu__btn',
    backdropSelector: '.c-drawer__backdrop',
    $buttons() {
      return document.querySelectorAll(this.btnSelector);
    },
    $backdrops() {
      return document.querySelectorAll(this.backdropSelector);
    },
    exists() {
      return this.$buttons().length > 0;
    },
  };

  // Private: Merge passed in object with defaults
  const _settings = {
    ..._defaults,
    ...options,
  };

  // Private: Are we below the desktop breakpoint?
  const _isMobileOrTablet = () => window.innerWidth < DESKTOP_BREAKPOINT;

  // Private: Open the mobile bottom drawer
  const _openDrawer = (drawer, backdrop) => {
    if (!drawer || !backdrop) return;

    drawer.style.display = 'block';
    backdrop.style.display = 'block';

    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      drawer.classList.add('open');
      backdrop.classList.add('open');
    });
  };

  // Private: Close the mobile bottom drawer
  const _closeDrawer = (drawer, backdrop) => {
    if (!drawer || !backdrop) return;

    drawer.classList.remove('open');
    backdrop.classList.remove('open');

    setTimeout(() => {
      drawer.style.display = 'none';
      backdrop.style.display = 'none';
    }, 300);

    document.body.style.overflow = '';
  };

  // Private: Toggle the desktop menu / open the mobile drawer
  const _buttonClickHandler = function(event) {
    event.preventDefault();
    event.stopPropagation();

    const container = this.closest('.c-table__actionsInner');
    const menu = container.querySelector('.c-overflowMenu');
    const drawer = container.querySelector('.c-drawer');
    const backdrop = container.querySelector('.c-drawer__backdrop');

    if (_isMobileOrTablet()) {
      closeAllMenus();
      closeAllDrawers(drawer);

      _openDrawer(drawer, backdrop);

      return;
    }

    document.querySelectorAll('.c-overflowMenu').forEach(otherMenu => {
      if (otherMenu !== menu) {
        otherMenu.style.display = 'none';
        _clearMenuPosition(otherMenu);
      }
    });

    const isOpen = menu.style.display === 'block';
    _stopTrackingMenuPosition();

    if (isOpen) {
      menu.style.display = 'none';
      _clearMenuPosition(menu);
      return;
    }

    menu.style.display = 'block';
    _positionMenu(this, menu);

    _repositionMenu = () => _positionMenu(this, menu);
    window.addEventListener('scroll', _repositionMenu, true);
    window.addEventListener('resize', _repositionMenu);
  };

  // Private: Close the drawer when its backdrop is clicked
  const _backdropClickHandler = function(event) {
    event.stopPropagation();

    const container = this.closest('.c-table__actionsInner');
    const drawer = container.querySelector('.c-drawer');

    _closeDrawer(drawer, this);
  };

  // Private: Close open desktop menus when clicking outside of them
  const _documentClickHandler = () => {
    if (!_isMobileOrTablet()) closeAllMenus();
  };

  // Private: Close everything on Escape
  const _documentKeydownHandler = event => {
    if (event.key === 'Escape') {
      closeAllMenus();
      closeAllDrawers();
    }
  };

  // Public: Destroy module instance
  const destroy = () => {
    _settings.$buttons().forEach($button => {
      $button.removeEventListener('click', _buttonClickHandler);
    });

    _settings.$backdrops().forEach($backdrop => {
      $backdrop.removeEventListener('click', _backdropClickHandler);
    });

    document.removeEventListener('click', _documentClickHandler);
    document.removeEventListener('keydown', _documentKeydownHandler);
  };

  // Public: Destroy module instance and run initialise again
  const reinit = () => {
    destroy();
    init();
  };

  // Public: Initialise module
  const init = () => {
    if (!_settings.exists()) return;

    _settings.$buttons().forEach($button => {
      $button.addEventListener('click', _buttonClickHandler);
    });

    _settings.$backdrops().forEach($backdrop => {
      $backdrop.addEventListener('click', _backdropClickHandler);
    });

    document.addEventListener('click', _documentClickHandler);
    document.addEventListener('keydown', _documentKeydownHandler);
  };

  // Return public methods
  return {
    destroy,
    reinit,
    init,
  };
}
